import { useEffect, useMemo, useRef, useState } from 'react';
import { Cloud, CloudOff, RadioTower } from 'lucide-react';

const API = (import.meta as any).env.VITE_BACKEND_URL;

type ConnectivityState = 'checking' | 'online' | 'offline';
type BadgePosition = { x: number; y: number };

const BADGE_POSITION_KEY = 'r2-connectivity-badge-position';

function defaultPosition(): BadgePosition {
  if (typeof window === 'undefined') return { x: 18, y: 16 };
  return { x: Math.max(12, window.innerWidth - 204), y: 16 };
}

function clampPosition(pos: BadgePosition, width = 188, height = 58): BadgePosition {
  if (typeof window === 'undefined') return pos;
  return {
    x: Math.min(Math.max(8, pos.x), Math.max(8, window.innerWidth - width - 8)),
    y: Math.min(Math.max(8, pos.y), Math.max(8, window.innerHeight - height - 8)),
  };
}

function readSavedPosition(): BadgePosition {
  if (typeof window === 'undefined') return defaultPosition();
  try {
    const raw = window.localStorage.getItem(BADGE_POSITION_KEY);
    if (!raw) return defaultPosition();
    const parsed = JSON.parse(raw) as BadgePosition;
    if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') return defaultPosition();
    return clampPosition(parsed);
  } catch {
    return defaultPosition();
  }
}

export function R2ConnectivityBadge(): JSX.Element {
  const [state, setState] = useState<ConnectivityState>('checking');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [position, setPosition] = useState<BadgePosition>(() => readSavedPosition());
  const [dragging, setDragging] = useState(false);
  const badgeRef = useRef<HTMLButtonElement | null>(null);
  const dragStartRef = useRef<{ pointerId: number; x: number; y: number; originX: number; originY: number } | null>(null);
  const didDragRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    let controller: AbortController | null = null;
    const check = async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        const timeout = window.setTimeout(() => controller?.abort(), 4500);
        const response = await fetch(`${API}/api/health`, { signal: controller.signal, cache: 'no-store' });
        window.clearTimeout(timeout);
        if (!mounted) return;
        setState(response.ok ? 'online' : 'offline');
        setLastChecked(new Date());
      } catch {
        if (!mounted) return;
        setState('offline');
        setLastChecked(new Date());
      }
    };
    void check();
    const timer = window.setInterval(() => void check(), 15000);
    const online = () => void check();
    const offline = () => { setState('offline'); setLastChecked(new Date()); };
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      mounted = false;
      controller?.abort();
      window.clearInterval(timer);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      setPosition((current) => {
        const next = clampPosition(current, badgeRef.current?.offsetWidth, badgeRef.current?.offsetHeight);
        try { window.localStorage.setItem(BADGE_POSITION_KEY, JSON.stringify(next)); } catch { /* non-blocking */ }
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const copy = useMemo(() => {
    if (state === 'online') return { label: 'R2 ONLINE', sub: 'systems linked', icon: <Cloud size={15} />, color: '#65d987', glow: 'rgba(101,217,135,0.52)' };
    if (state === 'offline') return { label: 'R2 OFFLINE', sub: 'local mode', icon: <CloudOff size={15} />, color: '#ff7b5c', glow: 'rgba(255,123,92,0.45)' };
    return { label: 'R2 CHECKING', sub: 'pinging core', icon: <RadioTower size={15} />, color: '#f4c95d', glow: 'rgba(244,201,93,0.44)' };
  }, [state]);

  return (
    <button
      type="button"
      data-testid="r2-connectivity-badge"
      aria-live="polite"
      aria-label={`${copy.label}. Open Mission Control health details.`}
      title={lastChecked ? `Last checked ${lastChecked.toLocaleTimeString()} · Open Mission Control` : 'Checking R2 status · Open Mission Control'}
      ref={badgeRef}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        const target = event.currentTarget;
        dragStartRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, originX: position.x, originY: position.y };
        didDragRef.current = false;
        setDragging(true);
        target.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        const start = dragStartRef.current;
        if (!start || start.pointerId !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDragRef.current = true;
        setPosition(clampPosition({ x: start.originX + dx, y: start.originY + dy }, badgeRef.current?.offsetWidth, badgeRef.current?.offsetHeight));
      }}
      onPointerUp={(event) => {
        const start = dragStartRef.current;
        if (start?.pointerId === event.pointerId) {
          const next = clampPosition(position, badgeRef.current?.offsetWidth, badgeRef.current?.offsetHeight);
          setPosition(next);
          try { window.localStorage.setItem(BADGE_POSITION_KEY, JSON.stringify(next)); } catch { /* non-blocking */ }
          dragStartRef.current = null;
          setDragging(false);
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }}
      onPointerCancel={(event) => {
        if (dragStartRef.current?.pointerId === event.pointerId) {
          dragStartRef.current = null;
          setDragging(false);
        }
      }}
      onClick={(event) => {
        if (didDragRef.current) {
          event.preventDefault();
          event.stopPropagation();
          setTimeout(() => { didDragRef.current = false; }, 0);
          return;
        }
        window.location.hash = 'r2-mission-control';
      }}
      style={{ position: 'fixed', left: position.x, top: position.y, zIndex: 99920, border: 0, padding: 0, background: 'transparent', cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none' }}
    >
      <style>{`
        @keyframes r2-badge-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
        @keyframes r2-badge-scan { 0% { transform: translateX(-130%); opacity: .1; } 50% { opacity: .82; } 100% { transform: translateX(130%); opacity: .1; } }
        @keyframes r2-badge-pulse { 0%,100% { box-shadow: 0 0 14px ${copy.glow}; } 50% { box-shadow: 0 0 30px ${copy.glow}; } }
      `}</style>
      <div style={{ position: 'relative', minWidth: 168, overflow: 'hidden', borderRadius: 14, border: `1px solid ${copy.color}66`, background: 'linear-gradient(135deg, rgba(4,10,24,0.92), rgba(8,18,32,0.76))', backdropFilter: 'blur(18px)', boxShadow: `0 18px 42px rgba(0,0,0,0.42), 0 0 22px ${copy.glow}`, transform: 'none', animation: dragging ? 'r2-badge-pulse 2.8s ease-in-out infinite' : 'r2-badge-float 5.2s ease-in-out infinite, r2-badge-pulse 2.8s ease-in-out infinite' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '16px 16px', opacity: 0.26 }} />
        <div style={{ position: 'absolute', top: 0, bottom: 0, width: 72, background: `linear-gradient(90deg, transparent, ${copy.color}35, transparent)`, animation: 'r2-badge-scan 3.4s ease-in-out infinite' }} />
        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: '34px 1fr', gap: 9, alignItems: 'center', padding: '10px 12px' }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, display: 'grid', placeItems: 'center', color: copy.color, border: `1px solid ${copy.color}55`, background: `${copy.color}14`, transform: 'none' }}>{copy.icon}</div>
          <div style={{ transform: 'none' }}>
            <div style={{ color: copy.color, fontSize: 11, letterSpacing: '0.16em', fontWeight: 950, lineHeight: 1 }}>{copy.label}</div>
            <div style={{ color: 'rgba(226,232,240,0.68)', fontSize: 10, marginTop: 4 }}>{copy.sub}</div>
          </div>
        </div>
      </div>
    </button>
  );
}
